/* Scoped accessibility and request guards around Streamline's native controls. */
(() => {
  'use strict';
  if (window.KookMobileNavigation) return;
  window.KookMobileNavigation = true;

  const observers = new WeakMap();
  const searches = new WeakMap();
  let searchId = 0;
  let searchTrigger = null;
  let cartAppsContainer = null;
  let cartAppsObserver;
  let priceRangePatched = false;
  let reservation = null;
  let reservationStyles;
  let reservationSize;
  let reservationFrame = 0;
  let reservationSpace = 0;

  function cancelSearch(state) {
    state.request += 1;
    state.controller?.abort();
    state.controller = null;
    state.results.removeAttribute('aria-busy');
  }

  function connectSearch(search) {
    if (searches.has(search) || !search.input || !search.predictiveSearchResults) return searches.get(search);
    const state = { request: 0, controller: null, results: search.predictiveSearchResults };
    state.results.id = `KookSearchResults-${++searchId}`;
    search.input.setAttribute('aria-controls', state.results.id);
    search.input.setAttribute('aria-owns', state.results.id);
    search.input.setAttribute('aria-expanded', 'false');
    searches.set(search, state);
    // Invalidate requests immediately, before the native 300ms input debounce.
    search.input.addEventListener('input', () => {
      cancelSearch(state);
      if (!search.input.value.trim()) search.close();
    });
    return state;
  }

  function installSearchAdapter() {
    const prototype = customElements.get('predictive-search')?.prototype;
    if (!prototype) return;
    const nativeOpen = prototype.open;
    const nativeClose = prototype.close;

    prototype.open = function() {
      const state = connectSearch(this);
      if (!state || !this.input.value.trim()) return this.close();
      // AJAX markup has duplicate heading IDs and a mismatched suggestions
      // label in this theme version. Scope each list to its own actual heading.
      state.results.querySelectorAll('[role="listbox"]').forEach((list, index) => {
        const heading = list.closest('.results')?.querySelector('h3');
        if (!heading) return;
        heading.id = `${state.results.id}-heading-${index}`;
        heading.setAttribute('data-kook-search-heading', '');
        list.setAttribute('aria-labelledby', heading.id);
      });
      nativeOpen.call(this);
      this.input.setAttribute('aria-expanded', 'true');
    };

    prototype.close = function() {
      const state = connectSearch(this);
      if (state) cancelSearch(state);
      nativeClose.call(this);
      this.input.setAttribute('aria-expanded', 'false');
    };

    prototype.getSearchResults = function(searchTerm) {
      const state = connectSearch(this);
      if (!state || this.enabled === 'false' || !searchTerm || (this.SearchModal && !this.SearchModal.classList.contains('modal--is-active'))) return this.close();
      cancelSearch(state);
      const request = state.request;
      const controller = new AbortController();
      state.controller = controller;
      state.results.setAttribute('aria-busy', 'true');
      const params = new URLSearchParams({ q: searchTerm, 'resources[limit]': 3, 'resources[limit_scope]': 'each', section_id: 'search-results' });
      const current = () => this.isConnected && request === state.request && this.input.value.trim() === searchTerm;

      fetch(`${window.theme.routes.predictiveSearch}?${params}`, { signal: controller.signal })
        .then(response => {
          if (!response.ok) throw new Error(`Search response ${response.status}`);
          return response.text();
        })
        .then(text => {
          if (!current()) return;
          const markup = new DOMParser().parseFromString(text, 'text/html').querySelector('#shopify-section-search-results');
          if (!markup) throw new Error('Search results are unavailable');
          state.results.innerHTML = markup.innerHTML;
          this.open();
          window.AOS?.refreshHard();
        })
        .catch(error => {
          // Aborted/obsolete responses must not reopen or replace newer results.
          if (error.name !== 'AbortError' && current()) this.close();
        })
        .finally(() => {
          if (request === state.request) {
            state.controller = null;
            state.results.removeAttribute('aria-busy');
          }
        });
    };

    document.querySelectorAll('predictive-search').forEach(connectSearch);
  }

  function labelPriceRanges(scope = document) {
    scope.querySelectorAll('.price-range').forEach(range => {
      range.querySelectorAll('[role="slider"]').forEach((handle, index) => {
        handle.setAttribute('aria-label', index === 0 ? 'Minimum price' : 'Maximum price');
      });
    });
  }

  function connectPriceRanges() {
    const prototype = window.theme?.PriceRange?.prototype;
    if (!priceRangePatched && prototype?.createPriceRange) {
      const create = prototype.createPriceRange;
      prototype.createPriceRange = function() {
        const slider = create.apply(this, arguments);
        labelPriceRanges(this.container.parentElement || document);
        return slider;
      };
      priceRangePatched = true;
    }
    labelPriceRanges();
  }

  function connectCartApps() {
    const container = document.querySelector('main .cart__empty-text')?.parentElement;
    const sync = () => document.querySelectorAll('[data-kook-cart-apps]').forEach(app => {
      app.hidden = !!cartAppsContainer?.classList.contains('is-empty');
    });
    if (container !== cartAppsContainer) {
      cartAppsObserver?.disconnect();
      cartAppsContainer = container;
      if (container) {
        cartAppsObserver = new MutationObserver(sync);
        cartAppsObserver.observe(container, { attributes: true, attributeFilter: ['class'] });
      }
    }
    if (container) sync();
  }

  function measureReservation() {
    reservationFrame = 0;
    let space = 0;
    if (reservation?.isConnected && reservation.classList.contains('bta-reservation-bottom')) {
      const box = reservation.getBoundingClientRect();
      const style = getComputedStyle(reservation);
      const showing = Number.parseFloat(reservation.style.bottom) >= 0;
      const stillVisible = box.top < window.innerHeight && box.bottom > 0;
      if (!reservation.hidden && style.display !== 'none' && style.visibility !== 'hidden' && (showing || stillVisible)) {
        space = Math.ceil(box.height);
      }
    }
    if (space !== reservationSpace) {
      document.documentElement.style.setProperty('--kook-reservation-space', `${space}px`);
      reservationSpace = space;
    }
  }

  function scheduleReservationMeasure() {
    if (!reservationFrame) reservationFrame = window.requestAnimationFrame(measureReservation);
  }

  function connectReservation() {
    const next = document.getElementById('bta-reservation');
    if (next === reservation) return;
    reservationStyles?.disconnect();
    reservationSize?.disconnect();
    reservation?.removeEventListener('transitionend', scheduleReservationMeasure);
    reservation = next;
    if (reservation) {
      reservationStyles = new MutationObserver(scheduleReservationMeasure);
      reservationStyles.observe(reservation, { attributes: true, attributeFilter: ['style', 'class', 'hidden'] });
      if (window.ResizeObserver) {
        reservationSize = new ResizeObserver(scheduleReservationMeasure);
        reservationSize.observe(reservation);
      }
      reservation.addEventListener('transitionend', scheduleReservationMeasure);
    }
    scheduleReservationMeasure();
  }

  function start() {
    connect();
    connectPriceRanges();
    connectCartApps();
    connectReservation();
    // BTA appends/replaces this wrapper directly under body. Avoid observing
    // every descendant or polling while the countdown updates its own iframe.
    new MutationObserver(connectReservation).observe(document.body, { childList: true });
    window.addEventListener('resize', scheduleReservationMeasure, { passive: true });
  }

  function connect(scope = document) {
    scope.querySelectorAll('[data-kook-nav-toggle]').forEach(button => {
      if (observers.has(button)) return;
      const sync = () => button.setAttribute('aria-expanded', String(button.classList.contains('is-active')));
      sync();
      const observer = new MutationObserver(sync);
      observer.observe(button, { attributes: true, attributeFilter: ['class'] });
      observers.set(button, observer);
    });
  }

  document.addEventListener('click', event => {
    const trigger = event.target.closest?.('.js-modal-open-search-modal');
    if (trigger) searchTrigger = trigger;
  }, true);

  document.addEventListener('modalOpen.SearchModal', () => {
    document.querySelectorAll('#SearchModal predictive-search').forEach(search => {
      if (search.enabled !== 'false' && search.input?.value.trim()) search.getSearchResults(search.input.value.trim());
    });
  });

  document.addEventListener('modalClose.SearchModal', () => {
    document.querySelectorAll('#SearchModal predictive-search').forEach(search => search.close?.());
    document.querySelectorAll('.js-modal-open-search-modal').forEach(trigger => trigger.setAttribute('aria-expanded', 'false'));
    if (searchTrigger?.isConnected && !document.querySelector('.drawer--is-open')) searchTrigger.focus();
    searchTrigger = null;
  });

  // This asset precedes deferred theme.js. Its native ready event covers the
  // first slider initialization; later instances use the scoped factory hook.
  document.addEventListener('page:loaded', connectPriceRanges);
  document.addEventListener('collection:reloaded', () => labelPriceRanges());
  document.addEventListener('shopify:section:load', event => {
    connect(event.target);
    event.target.querySelectorAll('predictive-search').forEach(connectSearch);
    connectPriceRanges();
    connectCartApps();
  });
  document.addEventListener('shopify:section:unload', event => {
    event.target.querySelectorAll('[data-kook-nav-toggle]').forEach(button => {
      observers.get(button)?.disconnect();
      observers.delete(button);
    });
    event.target.querySelectorAll('predictive-search').forEach(search => {
      const state = searches.get(search);
      if (state) cancelSearch(state);
    });
    if (cartAppsContainer && event.target.contains(cartAppsContainer)) {
      cartAppsObserver?.disconnect();
      cartAppsContainer = null;
    }
  });

  customElements.whenDefined('predictive-search').then(installSearchAdapter);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start, { once: true });
  } else {
    start();
  }
})();
